import React, { useState, useEffect, useContext } from "react";
import { C } from "../lib/colors.js";
import { primitiveUiId, surface, ui } from "../lib/telemetry/instrument.js";
import { SurfaceContext } from "./surfaceContext.js";
import { LightbulbIcon, EyeIcon, BellIcon } from "./Icons.jsx";
import { IconClose, IconInfo } from "./ui/Icons.jsx";
import { DRAWER_HELP, markDrawerHelpSeen } from "../onboarding/drawerHelp.js";

// Shared centered-modal backdrop + panel shell, used by the AccountModal,
// SettingsModal, ForecastReportModal, BugReportModal, and EventModal
// (overlays.jsx) popups. Backdrop is a fixed full-viewport flex-center
// overlay that closes on click; the panel stops that click from bubbling
// (stopPropagation) so clicking inside the panel doesn't dismiss it.
// `zIndex` / `backdropStyle` / `panelStyle` let each caller reproduce its
// exact prior geometry (width, padding, boxShadow, overflow, etc.) — this
// is a pure chrome extraction, not a redesign, so every call site must
// pass whatever made its shell visually distinct before the extraction.
//
// `surface` (+ `surfaceKey`) names the telemetry surface (lib/telemetry/registry/surfaces.ts):
// the panel gets `data-surface` and `data-ui="<surface>.dialog"`, the backdrop (a click on it
// closes the modal) `data-ui="<surface>.dismiss"`, and the CloseButton / DrawerHelpToggle
// inside derive their ids from it. The panel's own id keeps a click on inert panel content
// from being read as the backdrop's. Attributes only — no pixels move.
export function ModalShell({
  onClose,
  zIndex = 400,
  backdropStyle,
  panelStyle,
  panelProps,
  surface,
  surfaceKey,
  children,
}) {
  const panelClassName = ["modal-shell-panel", panelProps?.className].filter(Boolean).join(" ");
  const shell = (
    <div
      className="modal-shell-backdrop"
      data-ui={primitiveUiId(surface, "dismiss")}
      onClick={onClose}
      style={{
        zIndex,
        // Backdrop is the SCROLL container, panel pinned to the top (not vertically
        // centered). At Large/XL display scale the root's CSS `zoom` scales `vh`, so
        // a panel capped at `maxHeight: 85vh` renders TALLER than the physical
        // viewport; vertically centered, its top clips above the screen with no way
        // to scroll to it. Top-aligning + `overflowY: auto` keeps the panel's top
        // reachable at every zoom (the fix the report modal pioneered, now shared by
        // every ModalShell popup — Settings, Account, Bug report, …). The `3vh`
        // padding gives short panels breathing room from the top edge.
        ...backdropStyle,
      }}
    >
      <div
        {...panelProps}
        {...surfaceAttrs(surface, surfaceKey)}
        data-ui={primitiveUiId(surface, "dialog")}
        className={panelClassName}
        onClick={(e) => e.stopPropagation()}
        style={panelStyle}
      >
        {children}
      </div>
    </div>
  );
  // Always provided (null when untagged), so a modal opened inside a tagged drawer never
  // borrows the drawer's surface, and toggling `surface` never remounts the subtree.
  return <SurfaceContext.Provider value={surface || null}>{shell}</SurfaceContext.Provider>;
}

function surfaceAttrs(surface, surfaceKey) {
  if (!surface) return {};
  return surfaceKey === null || surfaceKey === undefined || surfaceKey === ""
    ? { "data-surface": surface }
    : { "data-surface": surface, "data-surface-key": String(surfaceKey) };
}

// Close "✕" button repeated across modals. Style is fully parameterized
// (fontSize/position/margins differ per site) — this only de-duplicates the
// markup + onClick wiring, not the visual result, which each caller pins to
// its pre-extraction value via `style`.
// Inside a tagged surface (ModalShell / OverlayPanel `surface`, or the `surface` prop here)
// it carries `data-ui="<surface>.close"`.
export function CloseButton({ onClick, style, dataPopupCancel, ariaLabel = "Close", surface }) {
  const enclosing = useContext(SurfaceContext);
  // Real <button> for keyboard accessibility (tab focus + Enter/Space). The
  // reset props (appearance/background/border/padding/margin/font/line-height)
  // neutralize the UA button chrome so it renders identically to the old <div>;
  // callers still pass positioning/size via `style`. The glyph-only ✕ has no
  // text content, so `aria-label` gives screen readers an accessible name.
  return (
    <button
      className="close-button"
      type="button"
      aria-label={ariaLabel}
      data-ui={primitiveUiId(surface || enclosing, "close")}
      {...(dataPopupCancel ? { "data-popup-cancel": "" } : {})}
      onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", ...style }}
    >
      {/* Sized by the caller's font size, as the glyph it replaces was. */}
      <IconClose style={{ width: "1em", height: "1em", display: "block" }} />
    </button>
  );
}

// Drawer/modal first-open help banner + its "?" reopen button, as a single
// toggle: shows DrawerHelp until the user's seen it (retired via
// drawerHelpState[k]), then shows the small reopen button in its place.
// `corner` positions the reopen button; `boxStyle` forwards to DrawerHelp
// (used by the Signal Library drawer to cap width under the chat console).
// The dismiss ✕ and the reopen ? carry `data-ui="<surface>.help-dismiss|help-reopen"` inside
// a tagged surface (the enclosing ModalShell / OverlayPanel, or the `surface` prop).
export function DrawerHelpToggle({
  k,
  corner,
  drawerHelpState,
  setDrawerHelpState,
  boxStyle,
  // Swaps the default copy for this key — for panels whose help text depends on a
  // feature flag. The same escape hatch DrawerHelp already offers direct callers.
  bodyOverride,
  surface,
}) {
  const enclosing = useContext(SurfaceContext);
  const surfaceName = surface || enclosing;
  return (
    <>
      {!drawerHelpState[k] && (
        <DrawerHelp
          k={k}
          boxStyle={boxStyle}
          bodyOverride={bodyOverride}
          surface={surfaceName}
          onClose={() => {
            markDrawerHelpSeen(k);
            setDrawerHelpState((s) => ({ ...s, [k]: true }));
          }}
        />
      )}
      {drawerHelpState[k] && (
        <HelpReopenButton
          corner={corner}
          surface={surfaceName}
          onClick={() => setDrawerHelpState((s) => ({ ...s, [k]: false }))}
        />
      )}
    </>
  );
}

export function OverlayPanel({
  side,
  open,
  width = 264,
  children,
  zOverride,
  // Skip the slide for this render: the rail switches sections in place, and a slide would
  // show the outgoing and incoming panels together.
  instant = false,
  className,
  "data-testid": testId,
  surface,
  surfaceKey,
}) {
  const l = side === "left";
  // Z-order across the four drawers: the most recently opened is on top —
  // App.jsx passes each drawer's slot from useDrawerStack (lib/drawerStack.js) as
  // `zOverride`, and a flyover opened on a drawer (Tracking Settings over the Forecast
  // Log) takes that drawer's slot + 1. The 50/60 defaults are only for a caller that
  // passes none.
  const z = typeof zOverride === "number" ? zOverride : l ? 50 : 60;
  // inert while closed: a closed drawer is only translated off-screen (+opacity 0,
  // pointerEvents:none) — it stays in the tab order and can hold focus. `inert`
  // removes it from focus/tab/AT and blurs any focused descendant, so keyboard
  // and screen-reader users can't land in an invisible panel. Bonus: it clears
  // the focus that let the browser scroll-into-view a closing panel (the
  // original trigger behind the drawer-shift bug 485fb71d) — defense in depth
  // atop the container's overflow:"clip".
  const panel = (
    <div
      data-testid={testId}
      {...surfaceAttrs(surface, surfaceKey)}
      className={className}
      inert={!open}
      style={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: l ? 0 : undefined,
        right: l ? undefined : 0,
        width,
        background: C.panel,
        zIndex: z,
        borderRight: l ? `1px solid ${C.edge}` : "none",
        borderLeft: l ? "none" : `1px solid ${C.edge}`,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: l ? `12px 0 32px ${C.shadow}` : `-12px 0 32px ${C.shadow}`,
        transform: open ? "translateX(0)" : l ? "translateX(-100%)" : "translateX(100%)",
        transition: instant
          ? "none"
          : open
            ? "transform var(--rail-slide)"
            : "transform var(--rail-slide), opacity 0s linear 260ms",
        pointerEvents: open ? "auto" : "none",
        opacity: open ? 1 : 0,
      }}
    >
      {children}
    </div>
  );
  return <SurfaceContext.Provider value={surface || null}>{panel}</SurfaceContext.Provider>;
}
// Blinking green lightbulb pinned over a tour-highlighted element. `target`
// matches a [data-tour-spotlight="<target>"] element; null hides it. Re-measures
// each frame so it tracks the timeline as it pans/zooms.
export function TourSpotlight({ target, chatWidth = 0, leftInset = 0, onNudgeToPie }) {
  // How many on-screen matches to highlight per target.
  const MAX = { trigger: 2, tab: 4 }[target] || 1;
  const [spots, setSpots] = useState([]);
  useEffect(() => {
    let raf;
    // No active spotlight (e.g. the report-open aside) — clear once (scheduled
    // via rAF, no sync setState in the effect body) and don't run the measure
    // loop at all; the per-frame loop below is only for tracking a live target
    // across timeline pans/zooms.
    if (!target) {
      raf = requestAnimationFrame(() => setSpots((s) => (s.length ? [] : s)));
      return () => cancelAnimationFrame(raf);
    }
    // Reveal-in-place gating: hold the bulb hidden until the target has STOPPED
    // moving — the timeline auto-zoom AND the marker's own popIn scale both settle
    // — so it fades in at its FINAL resting spot instead of tracking the pie
    // across the screen as it animates in (the "zig-zag fly-in"). Once shown,
    // resume per-frame tracking so it still follows the timeline on pan/zoom.
    let shown = false,
      stableFrames = 0,
      prevOut = null;
    const SETTLE_FRAMES = 4;
    // Sub-pixel tolerance (getBoundingClientRect + the zoom division yield
    // fractional px): strict equality could jitter forever and never reach
    // SETTLE_FRAMES, leaving the bulb permanently hidden. Also compare the raw
    // target center (cxRaw/cyRaw) that drives click-forwarding — if the bulb is
    // clamped (left/top unchanged) while the target moves, `spots` must still
    // update so a click forwards to the right element, not a stale one.
    const near = (x, y) => Math.abs((x || 0) - (y || 0)) < 0.5;
    const same = (a, b) =>
      !!b &&
      a.length === b.length &&
      a.every(
        (p, i) =>
          near(p.left, b[i].left) &&
          near(p.top, b[i].top) &&
          p.size === b[i].size &&
          near(p.cxRaw, b[i].cxRaw) &&
          near(p.cyRaw, b[i].cyRaw),
      );
    // First measure is scheduled via rAF (not called synchronously) so we don't
    // setState during the effect body.
    const tick = () => {
      // Several elements can carry the same target (every trigger / all the
      // edge tabs). Keep the on-screen ones (the first in DOM order may be an
      // off-screen/zero-size past event), up to MAX, and compute a final
      // bulb position that's clamped into the viewport.
      const out = [];
      for (const el of document.querySelectorAll(`[data-tour-spotlight="${target}"]`)) {
        // The app root is scaled by CSS `zoom` (e.g. 1.08 for the +1pt font bump).
        // getBoundingClientRect() reports POST-zoom SCREEN px, but this bulb is
        // position:fixed INSIDE the zoomed root, so whatever left/top we set gets
        // re-zoomed on render — drifting each bulb further off the more distant it
        // is from the origin (the "resolution way off to the right" bug). Work
        // entirely in PRE-zoom (local) coords: divide the measured rect + viewport
        // by the zoom ratio (rect.width / offsetWidth), so the root's re-zoom lands
        // the bulb back exactly on its target. Ratio is 1 in unzoomed themes → no-op.
        const raw = el.getBoundingClientRect();
        const zoom = el.offsetWidth ? raw.width / el.offsetWidth : 1;
        const r = {
          left: raw.left / zoom,
          top: raw.top / zoom,
          right: raw.right / zoom,
          bottom: raw.bottom / zoom,
          width: raw.width / zoom,
          height: raw.height / zoom,
        };
        const vw = window.innerWidth / zoom,
          vh = window.innerHeight / zoom;
        const cx = r.left + r.width / 2,
          cy = r.top + r.height / 2;
        if (r.width > 0 && r.height > 0 && cy > 8 && cx >= 0 && cx <= vw && cy <= vh) {
          // Triggers/pies on the timeline scroll under the right-side chat panel —
          // don't float their bulb over the chat (it should read as "behind" it).
          if (target !== "tab" && chatWidth && cx > vw - chatWidth) continue;
          // Same for the navigation rail's panel, which floats over the timeline's left.
          if (target !== "tab" && leftInset && cx < leftInset) continue;
          let left,
            top,
            size = 32;
          if (target === "tab") {
            // Forecast Log strip (tall + narrow) gets the full-size bulb; the
            // Contract (top) and Signal Library (bottom) bars get half-size ones.
            const isStrip = r.height > 200 && r.width < 80;
            if (isStrip) {
              left = r.right - 3;
              top = vh / 2 - 16;
              size = 32;
            } // Forecast Log, screen-centered
            else if (r.top < 60) {
              left = r.left + 31;
              top = r.bottom + 3;
              size = 16;
            } // Contract (top bar)
            else if (r.bottom > vh - 60) {
              left = r.left + 31;
              top = r.top - 22;
              size = 16;
            } // Signal Library (bottom bar)
            else {
              left = cx - 8;
              top = cy - 8;
              size = 16;
            }
          } else {
            const _dir = el.getAttribute("data-tour-trigger-dir");
            // A trigger's hit-area is a 4×-radius halo CENTERED on the pie, so the
            // pie fills the middle half of the rect (inset r.height/4 top & bottom);
            // the resolution rect IS the pie (no inset). Anchor to the pie's edge.
            const isTriggerT = target === "trigger";
            const inset = isTriggerT ? r.height / 4 : 0;
            const pieTop = r.top + inset,
              pieBottom = r.bottom - inset;
            const SZ = 32;
            // A trigger's TEXT LABEL sits just beyond the pie, and the bulb must clear
            // the WHOLE label, not just the pie edge. Both halves of that used to be
            // inferred from the event's direction ("yes" ⇒ label above, "no" ⇒ below)
            // with fixed clearances sized for a 2–3 line label. Neither inference holds
            // any more:
            //   SIDE: under the neutral (on-spine) treatment, labels ALTERNATE above and
            //   below the spine as a layout decision (App.jsx labelY) — unrelated to
            //   direction. A "yes" trigger whose label alternated BELOW got a bulb above,
            //   pointing at empty space.
            //   EXTENT: a label that wraps to 3 lines is taller than the fixed 63px
            //   below-clearance, so the bulb landed ON the last line.
            // So MEASURE the label instead: it decides both the side (which side of the
            // pie it occupies) and the distance (clear its actual box). The fixed
            // constants remain the fallback for a target with no rendered label — a
            // zoomed-out timeline below LABEL_MIN_PPD, and the final RESOLUTION, whose
            // % sits to the side and only needs a small hug over the pie.
            const ABOVE_CLEAR = isTriggerT ? 51 : 8; // clear a 2–3 line label above / small hug over the resolution pie
            const BELOW_CLEAR = 63; // clear a 2–3 line label below
            const LABEL_GAP = 10; // breathing room between the label's edge and the bulb
            const labelRaw = isTriggerT
              ? el.parentElement?.querySelector("[data-tour-label]")?.getBoundingClientRect()
              : null;
            // Same pre-zoom conversion as `r` above — a measured rect is post-zoom screen
            // px, and this bulb is positioned in the zoomed root's local coords.
            const label =
              labelRaw && labelRaw.height > 0
                ? { top: labelRaw.top / zoom, bottom: labelRaw.bottom / zoom }
                : null;
            // Compare CENTERS rather than edges: a label offset from a large pie can
            // overlap its bounding box on the near edge while still clearly sitting to
            // one side of it.
            const below = label ? (label.top + label.bottom) / 2 > cy : _dir === "no";
            left = cx - 16;
            if (label) {
              top = below ? label.bottom + LABEL_GAP : label.top - LABEL_GAP - SZ;
            } else {
              top = below ? pieBottom + BELOW_CLEAR : pieTop - ABOVE_CLEAR - SZ;
            }
            size = SZ;
          } // resolution + upward ("yes") triggers → bulb above (clear of the label); red ("no") triggers → below (clear of the label); centered on cx
          left = Math.max(4, Math.min(left, vw - 36));
          top = Math.max(4, Math.min(top, vh - 40));
          // Raw (pre-zoom-division = actual SCREEN) center of the target, kept so a
          // click on the bulb can be forwarded to whatever element sits at the
          // target (document.elementFromPoint → .click()) — i.e. clicking the bulb
          // acts like clicking the pie/trigger/tab it points at.
          out.push({
            left,
            top,
            size,
            dir: el.getAttribute("data-tour-trigger-dir"),
            cxRaw: raw.left + raw.width / 2,
            cyRaw: raw.top + raw.height / 2,
          });
          // Triggers: keep collecting so we can pick one blue + one red below.
          if (target !== "trigger" && out.length >= MAX) break;
        }
      }
      // Triggers: spotlight one blue (yes) and one red (no) so the user can branch
      // either direction. Fall back to the first two on-screen if only one color is.
      if (target === "trigger" && out.length) {
        const blue = out.find((s) => s.dir !== "no");
        const red = out.find((s) => s.dir === "no");
        const picked = [blue, red].filter(Boolean);
        const final = picked.length >= 2 ? picked : out.slice(0, 2);
        out.length = 0;
        out.push(...final);
      } else if (out.length > MAX) out.length = MAX;
      // Before first reveal: wait for `out` to be identical for SETTLE_FRAMES
      // consecutive frames (target no longer animating), then show it — it pops
      // in at its final location. After that, track every frame (keeping the
      // previous array when nothing moved so a static frame doesn't re-render).
      if (shown) {
        setSpots((s) => (same(out, s) ? s : out));
      } else if (out.length && same(out, prevOut)) {
        if (++stableFrames >= SETTLE_FRAMES) {
          shown = true;
          setSpots(out);
        }
      } else {
        stableFrames = 0;
        setSpots((s) => (s.length ? [] : s)); // hide any stale bulb from a prior target while we wait to settle
      }
      prevOut = out;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, MAX, chatWidth, leftInset]);
  if (!spots.length) return null;
  // "Soft Pulse Ring": the bulb stays still; a ring grows out of it (fades in then
  // out) — a calmer "look here" cue than the old blink. See Soft-Pulse-Ring handoff.
  // Clicking a bulb ACTS LIKE clicking the element it points at: forward the click
  // to whatever sits at the target's screen center (elementFromPoint → .click()),
  // which bubbles to that element's handler (resolution → open report; trigger →
  // open the event modal; tab → toggle the drawer). cxRaw/cyRaw are screen px, and
  // the bulb is OFFSET above/below the target, so elementFromPoint hits the pie,
  // never the bulb itself.
  const forwardClick = (p) => {
    const t = document.elementFromPoint(p.cxRaw, p.cyRaw);
    if (t && typeof t.click === "function") t.click();
  };
  // TIMELINE bulbs (trigger / resolution pies) are a POINTER, not a proxy: clicking
  // one nudges the user to click the actual pie (via onNudgeToPie) rather than
  // forwarding the click, so they learn where the real control is. Every OTHER
  // target (the edge-drawer "tab" cue, and control cues like bug-icon /
  // settings-gear) forwards the click to activate the underlying control — so this
  // is an explicit allowlist of the pie targets, not "everything but tab".
  const isTimeline = target === "trigger" || target === "resolution";
  // The old hint said "pie chart" for BOTH timeline targets. A trigger is still drawn as
  // a pie, but the resolution marker is a two-bar distribution under the neutral
  // treatment — and the tour's own narration now calls it a bar chart, so a hint saying
  // "pie" contradicts the line that sent the user here.
  const timelineHint =
    target === "resolution" ? "Click the bar chart itself" : "Click the trigger itself";
  const handleClick = (p) => {
    if (isTimeline && onNudgeToPie) {
      onNudgeToPie(target);
    } else {
      forwardClick(p);
    }
  };
  // Real <button> (not a div) so the cue is keyboard-focusable and Enter/Space
  // activate it — native button semantics; the reset props strip the UA chrome
  // so it renders identically to the old div overlay.
  return (
    <>
      {spots.map((p, i) => {
        const sz = p.size || 32;
        return (
          <button
            key={i}
            type="button"
            data-tour-spotlight-overlay={target}
            {...surface("onboarding")}
            {...ui("tour.spotlight-cue", target)}
            onClick={() => handleClick(p)}
            title={isTimeline ? timelineHint : "Click to select"}
            aria-label={
              isTimeline
                ? `Tour hint — ${timelineHint.toLowerCase()}`
                : "Tour hint — activate the highlighted item"
            }
            style={{
              position: "fixed",
              left: p.left,
              top: p.top,
              width: sz,
              height: sz,
              // TIMELINE bulbs sit BELOW the 500-band overlays the user summons — the
              // trigger hover tooltip, the citation card, the add-custom-event popover.
              // At 600 the bulb (and its pulsing ring, which grows past the icon) painted
              // straight over the tooltip the tour had just told the user to go read.
              // A persistent hint must yield to the thing it was pointing at. Non-timeline
              // cues (drawer tabs, bug icon, settings gear) keep 600: they highlight
              // chrome that those overlays never cover, and must stay above the drawers.
              zIndex: isTimeline ? 450 : 600,
              pointerEvents: "auto",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "popIn 0.35s ease-out",
              appearance: "none",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            <span
              data-tour-cue-ring=""
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                width: sz,
                height: sz,
                border: `2px solid ${C.green}`,
                borderRadius: "50%",
                transform: "translate(-50%,-50%) scale(1)",
                opacity: 0,
                pointerEvents: "none",
                animation: "tipCuePulse 4.8s ease-out infinite",
              }}
            />
            <LightbulbIcon size={sz} color={C.gold} />
          </button>
        );
      })}
    </>
  );
}

// First-open help shown at the top of a drawer or modal: a quiet info note (surface 2, a 1px
// line, a small accent info icon), never a callout. It stays visible while the drawer is open;
// after ~5s open it's marked seen (persisted per-drawer) and it retires when the drawer closes
// (see the drawer-help effect). Callers that pass `onClose` also get a dismiss button, which
// persists it as seen and swaps in the small "Show tip" button. The dismiss carries
// `data-ui="<surface>.help-dismiss"` from the `surface` prop, else the enclosing tagged surface.
export function DrawerHelp({ k, boxStyle, bodyOverride, onClose, surface }) {
  const enclosing = useContext(SurfaceContext);
  const h = DRAWER_HELP[k];
  if (!h) return null;
  const body = bodyOverride || h.body;
  // "\n" becomes a paragraph break; the "{eye}" token is kept for older copy and renders as
  // the watchlist eye inline.
  const renderedBody = body.split("\n").map((line, li) =>
    line === "" ? null : (
      <span key={li} style={{ display: "block", marginTop: li > 0 ? 4 : 0 }}>
        {line.split("{eye}").map((part, pi) => (
          <span key={pi}>
            {pi > 0 && (
              <span style={{ display: "inline-flex", verticalAlign: -2, margin: "0 2px" }}>
                <EyeIcon on size={13} />
              </span>
            )}
            {part}
          </span>
        ))}
      </span>
    ),
  );
  return (
    <div
      role="note"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        margin: "12px 12px 4px",
        padding: "10px 36px 10px 12px",
        background: C.rowInactive,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        ...boxStyle,
      }}
    >
      <IconInfo width={16} height={16} style={{ color: C.accent, flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: C.white, lineHeight: "18px" }}>
          {h.title}
        </div>
        <div style={{ fontSize: 13, color: C.grayL, lineHeight: 1.5, marginTop: 2 }}>
          {renderedBody}
        </div>
      </div>
      {onClose && (
        <button
          className="sy-iconbtn"
          type="button"
          data-ui={primitiveUiId(surface || enclosing, "help-dismiss")}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          title="Dismiss"
          aria-label="Dismiss tip"
          style={{ position: "absolute", top: 6, right: 6, width: 24, height: 24, padding: 0 }}
        >
          <IconClose style={{ width: 14, height: 14 }} />
        </button>
      )}
    </div>
  );
}

// The small "Show tip" button left in a drawer once its help note has been dismissed. Clicking
// it brings the note back. `corner` pins it to a panel corner: "br" | "bl" | "tr" (default) |
// "tl"; the panel must be positioned for it to anchor. It carries
// `data-ui="<surface>.help-reopen"` from the `surface` prop, else the enclosing tagged surface.
export function HelpReopenButton({ onClick, corner = "tr", style, surface }) {
  const enclosing = useContext(SurfaceContext);
  const pos =
    corner === "br"
      ? { bottom: 10, right: 12 }
      : corner === "bl"
        ? { bottom: 10, left: 12 }
        : corner === "tl"
          ? { top: 10, left: 12 }
          : { top: 10, right: 12 };
  return (
    <button
      className="sy-iconbtn"
      type="button"
      data-ui={primitiveUiId(surface || enclosing, "help-reopen")}
      onClick={onClick}
      title="Show tip"
      aria-label="Show tip"
      style={{
        position: "absolute",
        zIndex: 40,
        width: 24,
        height: 24,
        padding: 0,
        color: C.gray,
        ...pos,
        ...style,
      }}
    >
      <IconInfo style={{ width: 16, height: 16 }} />
    </button>
  );
}
